import {
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import type { RemoteI18nController } from "./remote-i18n";

// Bootstrap/recovery copy cannot depend on the backend it is recovering.
const copy = {
  es: {
    loading: "Cargando traducciones…",
    error: "Error al cargar traducciones.",
    retry: "Reintentar",
    timeout: "La carga de traducciones está tardando demasiado.",
    reload: "Recargar página",
  },
  en: {
    loading: "Loading translations…",
    error: "Failed to load translations.",
    retry: "Retry",
    timeout: "Translations are taking too long to load.",
    reload: "Reload page",
  },
};

export function RemoteI18nGuard({
  controller,
  locale,
  children,
  fallback,
}: {
  controller: RemoteI18nController;
  locale?: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );
  const [timedOut, setTimedOut] = useState(false);
  const [hasShownContent, setHasShownContent] = useState(
    snapshot.status === "ready",
  );
  const messages = (locale ?? snapshot.locale)?.startsWith("en")
    ? copy.en
    : copy.es;

  useEffect(() => {
    if (snapshot.status === "ready") setHasShownContent(true);
  }, [snapshot.status]);

  useEffect(() => {
    void controller.start(locale).catch(() => undefined);
  }, [controller, locale]);

  useEffect(() => {
    setTimedOut(false);
    if (snapshot.status !== "loading") return;
    // Do not abandon a pending init and start another on the same mutable instance.
    const timer = window.setTimeout(() => setTimedOut(true), 30_000);
    return () => window.clearTimeout(timer);
  }, [snapshot]);

  const showContent = hasShownContent || snapshot.status === "ready";
  const stalled = timedOut && snapshot.status === "loading";
  const recovery =
    snapshot.status === "error" || stalled ? (
      <div role="alert" className="p-4 text-center text-state-danger">
        <p>{stalled ? messages.timeout : messages.error}</p>
        <button
          className="mt-2 underline"
          onClick={() => {
            if (stalled) window.location.reload();
            else
              void controller
                .initializeI18n({
                  locale: locale ?? snapshot.locale,
                  retry: true,
                })
                .catch(() => undefined);
          }}
        >
          {stalled ? messages.reload : messages.retry}
        </button>
      </div>
    ) : null;
  // Keep forms and feature state mounted during warm locale changes and recovery.
  return (
    <>
      {recovery ??
        (!showContent
          ? (fallback ?? (
              <div role="status" aria-live="polite">
                {messages.loading}
              </div>
            ))
          : null)}
      {showContent ? children : null}
    </>
  );
}
